import {
  BackSide,
  CanvasTexture,
  Color,
  CylinderGeometry,
  DirectionalLight,
  Fog,
  Group,
  HemisphereLight,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshLambertMaterial,
  PlaneGeometry,
  RepeatWrapping,
  RingGeometry,
  Scene,
  SphereGeometry,
  SRGBColorSpace,
  TorusGeometry,
  Vector3,
} from 'three'
import { generateCity, type City } from '../world/cityGen'
import { buildCity } from './city'
import { CityCollision, FlatGround, type ContactWorld } from '../physics/collision'

export type WorldKind = 'flat' | 'city'

export interface PadInfo {
  position: Vector3
  radius: number
}

export interface WorldModel {
  kind: WorldKind
  launch: PadInfo
  landing: PadInfo
  /** What the physics collides against — always agrees with what is drawn. */
  collision: ContactWorld
  city?: City
}

/**
 * `flat` is Phase 1's grid plane (§6): the right place to tune physics, because
 * nothing occludes the horizon and nothing can be blamed on the level.
 * `city` is Phase 2. Both are always available — `?world=flat` goes back to the
 * tuning environment when a number looks wrong.
 */
export function buildWorld(scene: Scene, kind: WorldKind = 'city'): WorldModel {
  return kind === 'city' ? buildCityWorld(scene) : buildFlatWorld(scene)
}

// ---------------------------------------------------------------------------
// shared dressing
// ---------------------------------------------------------------------------

function addSky(scene: Scene, top: string, mid: string, horizon: string, bottom: string): void {
  const canvas = document.createElement('canvas')
  canvas.width = 4
  canvas.height = 256
  const ctx = canvas.getContext('2d')!
  const gradient = ctx.createLinearGradient(0, 0, 0, 256)
  gradient.addColorStop(0, top)
  gradient.addColorStop(0.42, mid)
  gradient.addColorStop(0.5, horizon)
  gradient.addColorStop(1, bottom)
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, 4, 256)
  const texture = new CanvasTexture(canvas)
  texture.colorSpace = SRGBColorSpace
  scene.add(
    new Mesh(
      new SphereGeometry(1800, 24, 16),
      new MeshBasicMaterial({ map: texture, side: BackSide, fog: false }),
    ),
  )
}

function buildPad(position: Vector3, radius: number, colour: number, beaconHeight: number): Group {
  const group = new Group()
  group.position.copy(position)

  const deck = new Mesh(
    new CylinderGeometry(radius, radius, 0.28, 32),
    new MeshLambertMaterial({ color: 0x1d2836 }),
  )
  deck.position.y = 0.14
  group.add(deck)

  const ring = new Mesh(
    new RingGeometry(radius * 0.72, radius * 0.93, 32),
    new MeshBasicMaterial({ color: colour, transparent: true, opacity: 0.95, side: BackSide }),
  )
  ring.rotation.x = Math.PI / 2
  ring.position.y = 0.3
  group.add(ring)

  // Findable from altitude and at any attitude — §2a, the player has no
  // proprioceptive access to virtual-down and needs generous visual anchors.
  const beacon = new Mesh(
    new CylinderGeometry(0.09, 0.09, beaconHeight, 6),
    new MeshBasicMaterial({ color: colour, transparent: true, opacity: 0.3, fog: false }),
  )
  beacon.position.y = beaconHeight / 2
  group.add(beacon)

  return group
}

function buildRings(from: Vector3, to: Vector3, count = 4): Group {
  const group = new Group()
  const geometry = new TorusGeometry(3.0, 0.18, 8, 28)
  const material = new MeshBasicMaterial({ color: 0xffb454, transparent: true, opacity: 0.9 })
  const direction = new Vector3().subVectors(to, from)

  for (let i = 1; i <= count; i++) {
    const t = i / (count + 1)
    const ring = new Mesh(geometry, material)
    // Arc the route upward in the middle so the rings read as a flight path
    // rather than a straight line of hoops.
    const lift = Math.sin(t * Math.PI) * 18
    ring.position.copy(from).addScaledVector(direction, t).add(new Vector3(0, lift + 6, 0))
    ring.lookAt(ring.position.clone().add(direction))
    group.add(ring)
  }
  return group
}

// ---------------------------------------------------------------------------
// flat — the tuning environment
// ---------------------------------------------------------------------------

function buildFlatWorld(scene: Scene): WorldModel {
  scene.background = new Color(0x0a1020)
  scene.fog = new Fog(0x0a1020, 120, 900)

  scene.add(new HemisphereLight(0x9fc4ff, 0x1b2430, 1.6))
  const sun = new DirectionalLight(0xffe9c4, 1.5)
  sun.position.set(60, 120, 40)
  scene.add(sun)

  addSky(scene, '#0a1430', '#16243f', '#2a3550', '#080c15')
  scene.add(buildGrid())
  scene.add(buildHorizonBand())
  scene.add(buildPylons())

  const launch = { position: new Vector3(0, 0, 0), radius: 3.5 }
  const landing = { position: new Vector3(0, 0, -50), radius: 3.5 }

  scene.add(buildPad(launch.position, launch.radius, 0x6fd0ff, 40))
  scene.add(buildPad(landing.position, landing.radius, 0x7ee787, 40))
  scene.add(buildRings(launch.position, landing.position))

  return { kind: 'flat', launch, landing, collision: new FlatGround() }
}

/** One textured plane rather than a GridHelper: a single draw call, and the
 * mip chain keeps distant ground from aliasing into noise on the Quest. */
function buildGrid(): Mesh {
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = 512
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = '#111a26'
  ctx.fillRect(0, 0, 512, 512)

  ctx.strokeStyle = 'rgba(110, 160, 210, 0.20)'
  ctx.lineWidth = 2
  for (let i = 1; i < 10; i++) {
    const p = (i / 10) * 512
    ctx.beginPath()
    ctx.moveTo(p, 0)
    ctx.lineTo(p, 512)
    ctx.moveTo(0, p)
    ctx.lineTo(512, p)
    ctx.stroke()
  }
  ctx.strokeStyle = 'rgba(140, 205, 255, 0.75)'
  ctx.lineWidth = 5
  ctx.strokeRect(0, 0, 512, 512)

  const texture = new CanvasTexture(canvas)
  texture.wrapS = texture.wrapT = RepeatWrapping
  texture.repeat.set(200, 200)
  texture.anisotropy = 8
  texture.colorSpace = SRGBColorSpace

  const ground = new Mesh(new PlaneGeometry(2000, 2000), new MeshLambertMaterial({ map: texture }))
  ground.rotation.x = -Math.PI / 2
  return ground
}

/** A bright band at eye level so the horizon reads even when inverted. */
function buildHorizonBand(): Mesh {
  return new Mesh(
    new CylinderGeometry(1200, 1200, 6, 48, 1, true),
    new MeshBasicMaterial({
      color: 0x3d6ea8,
      transparent: true,
      opacity: 0.5,
      side: BackSide,
      fog: false,
    }),
  )
}

/** Vertical posts. Altitude and vertical speed are nearly unreadable over a
 * flat plane; something with height fixes that instantly. */
function buildPylons(): InstancedMesh {
  const spacing = 40
  const half = 5
  const count = (half * 2 + 1) ** 2 - 1
  const mesh = new InstancedMesh(
    new CylinderGeometry(0.35, 0.5, 26, 5),
    new MeshLambertMaterial({ color: 0x2b3a4d }),
    count,
  )
  const matrix = new Matrix4()
  let i = 0
  for (let x = -half; x <= half; x++) {
    for (let z = -half; z <= half; z++) {
      if (x === 0 && z === 0) continue
      matrix.makeTranslation(x * spacing + 18, 13, z * spacing + 18)
      mesh.setMatrixAt(i++, matrix)
    }
  }
  mesh.instanceMatrix.needsUpdate = true
  return mesh
}

// ---------------------------------------------------------------------------
// city — Phase 2
// ---------------------------------------------------------------------------

function buildCityWorld(scene: Scene): WorldModel {
  const city = generateCity()

  // Dusk. Lit windows need a dark sky to read, and a low sun gives the towers
  // long shading contrast so you can judge distance between them.
  scene.background = new Color(0x101827)
  scene.fog = new Fog(0x131c2e, 180, 1400)

  // Bright enough that facades keep visible surface tone. Silhouette-only
  // buildings look striking in a screenshot and are dangerous to fly in: with
  // no shading gradient across a wall there is no cue for how far away it is,
  // and closing speed on a tower is exactly what the player must judge.
  scene.add(new HemisphereLight(0x93b4e6, 0x232c40, 2.6))
  const sun = new DirectionalLight(0xffd2a1, 1.7)
  sun.position.set(-160, 190, 120)
  scene.add(sun)
  // Opposite fill so the shaded side of a tower is dark, never black.
  const fill = new DirectionalLight(0x7f9ad0, 0.55)
  fill.position.set(150, 90, -140)
  scene.add(fill)

  addSky(scene, '#0b1020', '#1d2740', '#3a3550', '#0a0e18')
  scene.add(buildStreets(city))
  scene.add(buildCity(city))

  const launch = {
    position: new Vector3(city.launch.x, city.launch.y, city.launch.z),
    radius: city.launch.radius,
  }
  const landing = {
    position: new Vector3(city.landing.x, city.landing.y, city.landing.z),
    radius: city.landing.radius,
  }

  // Beacons run down to street level so a pad is visible from the canyons too.
  scene.add(buildPad(launch.position, launch.radius, 0x6fd0ff, 120))
  scene.add(buildPad(landing.position, landing.radius, 0x7ee787, 120))
  scene.add(buildRings(launch.position, landing.position))

  return { kind: 'city', launch, landing, collision: new CityCollision(city.buildings), city }
}

function buildStreets(city: City): Mesh {
  const pitch = city.blockSize + city.streetWidth
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = 256
  const ctx = canvas.getContext('2d')!

  ctx.fillStyle = '#20242c'
  ctx.fillRect(0, 0, 256, 256)

  // One tile is one block pitch: asphalt cross with a painted centre line.
  const roadPx = (city.streetWidth / pitch) * 256
  ctx.fillStyle = '#31353d'
  ctx.fillRect(0, 0, 256, roadPx)
  ctx.fillRect(0, 0, roadPx, 256)

  ctx.strokeStyle = 'rgba(230, 200, 120, 0.5)'
  ctx.lineWidth = 2
  ctx.setLineDash([9, 11])
  ctx.beginPath()
  ctx.moveTo(0, roadPx / 2)
  ctx.lineTo(256, roadPx / 2)
  ctx.moveTo(roadPx / 2, 0)
  ctx.lineTo(roadPx / 2, 256)
  ctx.stroke()
  ctx.setLineDash([])

  const texture = new CanvasTexture(canvas)
  texture.wrapS = texture.wrapT = RepeatWrapping
  const span = 4000
  texture.repeat.set(span / pitch, span / pitch)
  // Line the tiling up with the block grid so roads meet building gaps.
  texture.offset.set(0.5 - city.streetWidth / pitch / 2, 0.5 - city.streetWidth / pitch / 2)
  texture.anisotropy = 8
  texture.colorSpace = SRGBColorSpace

  const ground = new Mesh(new PlaneGeometry(span, span), new MeshLambertMaterial({ map: texture }))
  ground.rotation.x = -Math.PI / 2
  return ground
}
